import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import {
  ArrowLeft, Search, Plus, Pencil, Trash2, RotateCcw, Package, Barcode, Tags, Store as StoreIcon, EyeOff, ChevronLeft, ChevronRight,
} from "lucide-react";
import {
  getAdminToken, adminMasterStats, adminMasterFacets, adminListMasterProducts, adminCreateMasterProduct,
  adminUpdateMasterProduct, adminDeleteMasterProduct, adminBulkMasterProducts,
} from "@/lib/api";
import { MasterProductModal } from "@/components/admin/MasterProductModal";
import { RenameTool } from "@/components/admin/RenameTool";

const LIMIT = 50;

export default function AdminCatalogo() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [facets, setFacets] = useState({ categories: [], brands: [] });
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("active");
  const [barcode, setBarcode] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [modal, setModal] = useState(null); // null | {} | item
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadMeta = useCallback(async () => {
    const [s, f] = await Promise.all([adminMasterStats(), adminMasterFacets()]);
    setStats(s); setFacets(f);
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminListMasterProducts({ q: q || undefined, category: category || undefined, status, barcode: barcode || undefined, page, limit: LIMIT });
      setItems(r.items); setTotal(r.total); setSelected(new Set());
    } catch (e) {
      if (e?.response?.status === 401) nav("/admin/login");
      else toast.error("No se pudo cargar el catálogo");
    } finally { setLoading(false); }
  }, [q, category, status, barcode, page, nav]);

  useEffect(() => { if (!getAdminToken()) nav("/admin/login"); else loadMeta().catch(() => {}); }, [nav, loadMeta]);
  useEffect(() => { const t = setTimeout(loadList, 250); return () => clearTimeout(t); }, [loadList]);
  useEffect(() => { setPage(1); }, [q, category, status, barcode]);

  const refresh = () => { loadList(); loadMeta().catch(() => {}); };

  const save = async (body) => {
    setSaving(true);
    try {
      if (modal?.id) { await adminUpdateMasterProduct(modal.id, body); toast.success("Producto actualizado"); }
      else { await adminCreateMasterProduct(body); toast.success("Producto creado en el catálogo global"); }
      setModal(null); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  const toggleActive = async (it) => {
    try {
      if (it.active) await adminDeleteMasterProduct(it.id);
      else await adminUpdateMasterProduct(it.id, { active: true });
      toast.success(it.active ? "Producto desactivado" : "Producto reactivado");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const hardDelete = async (it) => {
    if (!window.confirm(`¿Borrar definitivamente "${it.product_name}"? Solo se permite si ninguna tienda lo adoptó.`)) return;
    try { await adminDeleteMasterProduct(it.id, true); toast.success("Producto borrado"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo borrar"); }
  };

  const bulk = async (action) => {
    try {
      const r = await adminBulkMasterProducts([...selected], action);
      toast.success(`${r.modified} productos ${action === "activate" ? "activados" : "desactivados"}`);
      refresh();
    } catch { toast.error("Error en acción masiva"); }
  };

  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const pages = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <main className="min-h-screen bg-[#050505] text-white" data-testid="admin-catalogo">
      <div className="grain-overlay" />
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#050505]/85 backdrop-blur-xl">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-4">
          <Link to="/admin" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-white/50 hover:text-white" data-testid="admin-catalogo-back"><ArrowLeft size={14} /> Centro de control</Link>
          <button onClick={() => setModal({})} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#ff5a00] text-black text-[10px] font-mono uppercase tracking-widest hover:bg-[#ff7a2a]" data-testid="mp-new-btn"><Plus size={14} /> Nuevo producto</button>
        </div>
      </header>

      <div className="relative max-w-[1600px] mx-auto px-6 md:px-10 py-8 md:py-12">
        <p className="font-mono text-[11px] tracking-[0.3em] text-white/40 uppercase mb-2">/ Catálogo maestro</p>
        <h1 className="font-display text-4xl md:text-6xl tracking-tighter leading-none mb-8">Productos <span className="text-[#ff5a00]">globales</span>.</h1>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8" data-testid="mp-stats">
          <Stat icon={<Package size={14} />} label="SKUs activos" value={stats?.active} tid="mp-stat-active" />
          <Stat icon={<EyeOff size={14} />} label="Inactivos" value={stats?.inactive} tid="mp-stat-inactive" tint="text-white/50" />
          <Stat icon={<Barcode size={14} />} label="Con código de barras" value={stats?.with_barcode} tid="mp-stat-barcode" tint="text-[#ccff00]" />
          <Stat icon={<Tags size={14} />} label="Marcas" value={stats?.brands_count} tid="mp-stat-brands" />
          <Stat icon={<StoreIcon size={14} />} label="Adoptados por tiendas" value={stats?.adopted_skus} tid="mp-stat-adopted" tint="text-emerald-400" />
        </div>

        <RenameTool onDone={refresh} />

        <div className="mt-6 flex flex-col md:flex-row md:items-center gap-3 mb-4">
          <div className="flex-1 flex items-center gap-2 bg-black/60 border border-white/10 rounded-2xl px-3 py-2">
            <Search size={14} className="text-white/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar nombre, marca, alias o código…" className="flex-1 bg-transparent outline-none text-sm placeholder-white/30" data-testid="mp-search" />
          </div>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="bg-black/60 border border-white/10 rounded-2xl px-3 py-2 text-sm" data-testid="mp-filter-category">
            <option value="">Todas las categorías</option>
            {facets.categories.map((c) => <option key={c.category} value={c.category}>{`${c.category} · ${c.count}`}</option>)}
          </select>
          <div className="flex items-center gap-1">
            {[["active", "Activos"], ["inactive", "Inactivos"], ["all", "Todos"]].map(([k, l]) => (
              <button key={k} onClick={() => setStatus(k)} className={`px-3 py-2 rounded-full border text-[10px] font-mono uppercase tracking-widest ${status === k ? "border-white bg-white text-black" : "border-white/10 text-white/60 hover:text-white"}`} data-testid={`mp-status-${k}`}>{l}</button>
            ))}
          </div>
          <div className="flex items-center gap-1">
            {[["", "Todos"], ["with", "Con EAN"], ["without", "Sin EAN"]].map(([k, l]) => (
              <button key={k} onClick={() => setBarcode(k)} className={`px-3 py-2 rounded-full border text-[10px] font-mono uppercase tracking-widest ${barcode === k ? "border-[#ccff00] bg-[#ccff00] text-black" : "border-white/10 text-white/60 hover:text-white"}`} data-testid={`mp-barcode-${k || "all"}`}>{l}</button>
            ))}
          </div>
        </div>

        {selected.size > 0 && (
          <div className="mb-3 flex items-center gap-3 bg-[#ff5a00]/10 border border-[#ff5a00]/30 rounded-2xl px-4 py-2 text-sm" data-testid="mp-bulk-bar">
            <span className="font-mono text-[11px] uppercase tracking-widest text-[#ff5a00]">{selected.size} seleccionados</span>
            <button onClick={() => bulk("deactivate")} className="px-3 py-1 rounded-full border border-white/10 text-[10px] font-mono uppercase tracking-widest hover:text-white text-white/70" data-testid="mp-bulk-deactivate">Desactivar</button>
            <button onClick={() => bulk("activate")} className="px-3 py-1 rounded-full border border-emerald-500/30 text-emerald-400 text-[10px] font-mono uppercase tracking-widest hover:bg-emerald-500/10" data-testid="mp-bulk-activate">Activar</button>
          </div>
        )}

        <div className="border border-white/10 rounded-2xl overflow-hidden" data-testid="mp-table">
          <div className="grid grid-cols-[32px_minmax(220px,2fr)_minmax(140px,1fr)_minmax(140px,1fr)_130px_90px_150px] px-4 py-3 bg-white/5 text-[10px] font-mono uppercase tracking-widest text-white/50">
            <span /><span>Producto</span><span>Marca</span><span>Categoría</span><span>Código</span><span>Estado</span><span className="text-right pr-1">Acciones</span>
          </div>
          {loading ? <div className="p-8 text-center text-white/40 text-sm">Cargando…</div>
            : items.length === 0 ? <div className="p-8 text-center text-white/40 text-sm" data-testid="mp-empty">Sin resultados.</div>
            : items.map((it) => (
              <div key={it.id} className="grid grid-cols-[32px_minmax(220px,2fr)_minmax(140px,1fr)_minmax(140px,1fr)_130px_90px_150px] items-center px-4 py-3 border-t border-white/5 hover:bg-white/[0.02]" data-testid={`mp-row-${it.id}`}>
                <input type="checkbox" checked={selected.has(it.id)} onChange={() => toggleSel(it.id)} data-testid={`mp-select-${it.id}`} />
                <div className="min-w-0">
                  <p className="font-display text-base tracking-tight truncate" data-testid={`mp-name-${it.id}`}>{it.product_name}{it.variant ? ` · ${it.variant}` : ""}</p>
                  <p className="font-mono text-[10px] text-white/40 truncate">{it.presentation || (it.net_content_ml ? `${it.net_content_ml} ml/g` : "")}{it.pieces ? ` · ${it.pieces} pzas` : ""}{it.variable_weight ? " · granel" : ""}</p>
                </div>
                <span className="text-sm truncate">{it.brand || <span className="text-white/30">—</span>}</span>
                <div className="min-w-0"><p className="text-sm truncate">{it.category}</p><p className="font-mono text-[10px] text-white/40 truncate">{it.subcategory || ""}</p></div>
                <span className="font-mono text-[11px] text-white/60 truncate">{it.barcode || <span className="text-[#ff5a00]/70">sin EAN</span>}</span>
                <span className={`inline-flex px-2 py-1 rounded-full border text-[10px] font-mono uppercase tracking-widest w-fit ${it.active ? "border-emerald-500/30 text-emerald-400 bg-emerald-500/10" : "border-white/10 text-white/40"}`}>{it.active ? "Activo" : "Inactivo"}</span>
                <div className="flex items-center justify-end gap-1">
                  <IconBtn tid={`mp-edit-${it.id}`} title="Editar" onClick={() => setModal(it)}><Pencil size={13} /></IconBtn>
                  <IconBtn tid={`mp-toggle-${it.id}`} title={it.active ? "Desactivar" : "Reactivar"} onClick={() => toggleActive(it)}>{it.active ? <EyeOff size={13} /> : <RotateCcw size={13} />}</IconBtn>
                  <IconBtn tid={`mp-hard-delete-${it.id}`} title="Borrar definitivo" danger onClick={() => hardDelete(it)}><Trash2 size={13} /></IconBtn>
                </div>
              </div>
            ))}
        </div>

        <div className="mt-4 flex items-center justify-between text-[11px] font-mono uppercase tracking-widest text-white/50">
          <span data-testid="mp-total">{total} productos</span>
          <div className="flex items-center gap-2">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="p-2 rounded-full border border-white/10 disabled:opacity-30" data-testid="mp-prev"><ChevronLeft size={14} /></button>
            <span>Pág. {page} / {pages}</span>
            <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="p-2 rounded-full border border-white/10 disabled:opacity-30" data-testid="mp-next"><ChevronRight size={14} /></button>
          </div>
        </div>
      </div>

      {modal !== null && <MasterProductModal initial={modal} onClose={() => setModal(null)} onSave={save} saving={saving} />}
      <Toaster theme="dark" position="top-center" />
    </main>
  );
}

function Stat({ icon, label, value, tid, tint = "text-white" }) {
  return (
    <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-4" data-testid={tid}>
      <div className="flex items-center gap-1.5 text-white/40 text-[10px] font-mono uppercase tracking-widest"><span className={tint}>{icon}</span> {label}</div>
      <p className={`font-display text-3xl tracking-tighter mt-2 leading-none ${tint}`}>{value ?? "—"}</p>
    </div>
  );
}

function IconBtn({ children, onClick, title, danger, tid }) {
  return (
    <button data-testid={tid} onClick={onClick} title={title} className={`p-1.5 rounded-lg border text-[11px] transition-colors ${danger ? "border-red-500/30 text-red-400 hover:bg-red-500/10" : "border-white/10 text-white/70 hover:border-white/30 hover:text-white"}`}>
      {children}
    </button>
  );
}
